import {
	ListServiceTicketsResponse
} from "client/Api/Workspace/LiveInformation/ConnectWise/connectWiseLiveInformation";
import { BusyIndicator, DateString } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ConnectWiseLiveInformationProvider,
	InterestedConnectWiseParty
} from "client_legacy/Workspace/LiveInformation/ConnectWise/provider";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { ListItem } from "../ListItem";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { useCreateTicketDialog } from "./CreateTicketDialog";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type ConnectWiseLiveTileContentProps = {
	provider: ConnectWiseLiveInformationProvider;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ConnectWiseLiveTileContent = (props: ConnectWiseLiveTileContentProps): ReactElement => {
	const {
		provider,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const {
		id: tileId,
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [createTicketDialog, openCreateTicketDialog] = useCreateTicketDialog();
	const timezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	useEffect(
		(): (() => void) => {
			setLoadingState({ isLoading: true });

			const party: InterestedConnectWiseParty = {
				partyId: tileId,
				resourceId: "tickets",
				informAboutConnectWiseNotBeingConfigured: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				update: (response: ListServiceTicketsResponse) => {
					setLoadingState({
						isLoading: false,
						response
					});
				}
			};

			provider.register(party, { items: true });

			return (): void => provider.unregister(party);
		},
		[provider, tileId]
	);

	const titleSuffix = useMemo(
		(): string | undefined => {
			if (!provider.isConnectWiseConfigured
				|| loadingState.isLoading
				|| loadingState.hasGlobalFailure
				|| loadingState.hasTooManyConsecutiveErrors
			) {
				return undefined;
			}

			const { items, hasMore } = loadingState.response;

			let amountText: string = items.length.toString();
			if (hasMore) {
				amountText += "+";
			}

			return `(${amountText})`;
		},
		[loadingState, provider.isConnectWiseConfigured]
	);

	const newHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			openCreateTicketDialog();
		},
		[
			addAnalyticsEvent,
			openCreateTicketDialog
		]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[
			addAnalyticsEvent,
			onNavigate
		]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			new: newHandler
		}),
		[newHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			isConnectWiseConfigured: provider.isConnectWiseConfigured
		}),
		[provider.isConnectWiseConfigured]
	);

	const renderContent = (): ReactElement => {
		if (!provider.isConnectWiseConfigured) {
			return <LiveTileContent.Message text={localize("_IncompleteConfigurationPleaseContactYourAdmin")} />;
		}

		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasGlobalFailure) {
			return <LiveTileContent.Message text={localize("_UnknownErrorDuringRetrievalOfTickets")} />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return (
				<TileRefresh onRefresh={() => provider.forceReload()} />
			);
		}

		const { items } = loadingState.response;

		if (items.length === 0) {
			return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{items.map((x): ReactElement => (
						<ListItem
							key={x.id}
							trackingId={generateTrackingId("ticket", `|${x.id}`)}
							isHoverable
							onClick={navigateHandler}
							title={x.title}
						>
							<ListItem.Content>
								<ListItem.Row>
									<ListItem.Title>
										{x.title}
									</ListItem.Title>
								</ListItem.Row>

								<ListItem.Row>
									<ListItem.Info>
										{x.status}
									</ListItem.Info>

									{x.dateCreated && (
										<ListItem.RightAddition>
											<ListItem.Info>
												<DateString
													value={x.dateCreated}
													format="AbsoluteTimestamp"
													timezone={timezone}
												/>
											</ListItem.Info>
										</ListItem.RightAddition>
									)}
								</ListItem.Row>
							</ListItem.Content>
						</ListItem>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<LiveTileContent
				name={name}
				icon={icon}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
			>
				<LiveTileContent.Header
					icon={icon}
					actions={defaultActions}
					actionContext={actionContext}
				>
					<LiveTileContent.Title
						text={localize("_MyOpenTickets")}
						suffix={titleSuffix}
					/>
				</LiveTileContent.Header>

				<LiveTileContent.Content>
					<LiveTileContent.ContentStack>
						<LiveTileContent.BusyIndicatorContainer>
							{renderContent()}
						</LiveTileContent.BusyIndicatorContainer>
					</LiveTileContent.ContentStack>
				</LiveTileContent.Content>

				<LiveTileContent.Footer
					name={name}
					onClick={navigateHandler}
				/>
			</LiveTileContent>

			{createTicketDialog}
		</>
	);
};
ConnectWiseLiveTileContent.displayName = "ConnectWiseLiveTileContent";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors?: undefined;
			hasGlobalFailure?: undefined;
			response: ListServiceTicketsResponse;
		}
	);

type ActionContext = {
	isConnectWiseConfigured: boolean;
};

type ActionHandlers = {
	new: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-ticket",
			title: localize("_NewTicket"),
			icon: "plus",
			canExecute: (x) => x.isConnectWiseConfigured,
			execute: () => handlers.new()
		}
	];
}
